export const ANALYTICS_EVENT_NAMES = [
  "primary_cta_click",
  "secondary_cta_click",
  "booking_cta_click",
  "contact_form_start",
  "contact_form_submit",
  "readiness_scorecard_start",
  "readiness_scorecard_complete",
  "service_page_cta_click",
  "industry_page_cta_click",
  "insights_cta_click",
  "verification_offer_view",
  "verification_sample_view",
  "verification_resource_open",
  "verification_application_start",
  "verification_application_submit",
] as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[number];
export type AnalyticsPropertyValue = string | number | boolean | null | undefined;
export type AnalyticsProperties = Record<string, AnalyticsPropertyValue>;

type AnalyticsPayload = Record<string, string | number | boolean | null>;

declare global {
  interface Window {
    vertAiAnalytics?: {
      track?: (eventName: AnalyticsEventName, properties?: AnalyticsPayload) => void;
    };
    gtag?: (command: "event", eventName: AnalyticsEventName, properties?: AnalyticsPayload) => void;
    dataLayer?: Array<Record<string, unknown>>;
    plausible?: (eventName: AnalyticsEventName, options?: { props?: AnalyticsPayload }) => void;
    posthog?: {
      capture?: (eventName: AnalyticsEventName, properties?: AnalyticsPayload) => void;
    };
    analytics?: {
      track?: (eventName: AnalyticsEventName, properties?: AnalyticsPayload) => void;
    };
    va?: (command: "event", payload: { name: AnalyticsEventName; data?: AnalyticsPayload }) => void;
  }
}

const analyticsEventNameSet = new Set<string>(ANALYTICS_EVENT_NAMES);

const allowedPropertyKeys = new Set([
  "event_location",
  "event_surface",
  "event_target",
  "path",
  "target_path",
  "service_slug",
  "industry_slug",
  "article_slug",
  "resource_slug",
]);

const sensitiveKeyPattern = /(email|mail|name|phone|role|message|company|context|decision|deadline|answer|score|token|secret|password|credential|query|search|application)/i;
const emailLikePattern = /[^\s@]+@[^\s@]+\.[^\s@]+/;

export const isAnalyticsEventName = (eventName: string): eventName is AnalyticsEventName =>
  analyticsEventNameSet.has(eventName);

export const parseAnalyticsEventNames = (value?: string | null): AnalyticsEventName[] => {
  if (!value) return [];

  return Array.from(new Set(
    value
      .split(/[\s,]+/)
      .map((eventName) => eventName.trim())
      .filter(isAnalyticsEventName),
  ));
};

const sanitizePath = (value: string) => {
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://vert-ai.com";
    const url = new URL(value, origin);
    return url.pathname.replace(/\/+$/, "") || "/";
  } catch {
    const [path] = value.split(/[?#]/);
    return path.replace(/\/+$/, "") || "/";
  }
};

export const sanitizeAnalyticsProperties = (properties: AnalyticsProperties = {}): AnalyticsPayload => {
  const safeProperties: AnalyticsPayload = {};

  for (const [key, rawValue] of Object.entries(properties)) {
    if (!allowedPropertyKeys.has(key) || sensitiveKeyPattern.test(key) || rawValue === undefined) continue;

    if (rawValue === null || typeof rawValue === "boolean" || typeof rawValue === "number") {
      safeProperties[key] = rawValue;
      continue;
    }

    if (typeof rawValue !== "string") continue;

    const value = rawValue.trim();
    if (!value) continue;

    if (key === "path" || key === "target_path") {
      const path = sanitizePath(value);
      if (!emailLikePattern.test(path)) safeProperties[key] = path;
      continue;
    }

    if (key === "event_target" && /^(?:https?:\/\/|\/)/i.test(value)) {
      const path = sanitizePath(value);
      if (!emailLikePattern.test(path)) safeProperties[key] = path.slice(0, 120);
      continue;
    }

    if (value.includes("?")) continue;
    if (emailLikePattern.test(value)) continue;

    safeProperties[key] = value.slice(0, 120);
  }

  return safeProperties;
};

export const trackEvent = (eventName: AnalyticsEventName | string, properties?: AnalyticsProperties) => {
  if (!isAnalyticsEventName(eventName) || typeof window === "undefined") return false;

  const safeProperties = sanitizeAnalyticsProperties(properties);
  window.vertAiAnalytics?.track?.(eventName, safeProperties);
  window.gtag?.("event", eventName, safeProperties);
  window.dataLayer?.push({ event: eventName, ...safeProperties });
  window.plausible?.(eventName, { props: safeProperties });
  window.posthog?.capture?.(eventName, safeProperties);
  window.analytics?.track?.(eventName, safeProperties);
  window.va?.("event", { name: eventName, data: safeProperties });

  return true;
};

export const trackEvents = (eventNames: Array<AnalyticsEventName | string>, properties?: AnalyticsProperties) => {
  const trackedEvents = Array.from(new Set(eventNames));
  trackedEvents.forEach((eventName) => trackEvent(eventName, properties));
};
